import { useSearchParams } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useBreakpoint } from "../hooks/useBreakpoint";

const G = "#0891b2";

const PLAN_ORDER = ["core", "pro", "ultra"];

type PlanFeature = { label: string; included: boolean };

type PlanDef = {
  id: string;
  name: string;
  tagline: string;
  features: PlanFeature[];
};

const PLANS: PlanDef[] = [
  {
    id: "core",
    name: "CORE",
    tagline: "Lo esencial para empezar a monitorizar",
    features: [
      { label: "Monitorización de fuentes", included: true },
      { label: "Cambios detectados", included: true },
      { label: "Histórico", included: true },
      { label: "Radar Regulatorio", included: true },
      { label: "Portfolio y competidores", included: true },
      { label: "Alertas por email instantáneas", included: false },
      { label: "Resumen semanal por email", included: false },
      { label: "Predicciones de precio con IA", included: false },
      { label: "Comparador de precios vs. competencia", included: false },
      { label: "Ranking de oportunidad", included: false },
      { label: "5 usuarios", included: true },
    ],
  },
  {
    id: "pro",
    name: "PRO",
    tagline: "Todo CORE + alertas en tiempo real",
    features: [
      { label: "Monitorización de fuentes", included: true },
      { label: "Cambios detectados", included: true },
      { label: "Histórico", included: true },
      { label: "Radar Regulatorio", included: true },
      { label: "Portfolio y competidores", included: true },
      { label: "Alertas por email instantáneas", included: true },
      { label: "Resumen semanal por email", included: true },
      { label: "Predicciones de precio con IA", included: false },
      { label: "Comparador de precios vs. competencia", included: false },
      { label: "Ranking de oportunidad", included: false },
      { label: "10 usuarios", included: true },
    ],
  },
  {
    id: "ultra",
    name: "ULTRA",
    tagline: "Todo PRO + inteligencia predictiva",
    features: [
      { label: "Monitorización de fuentes", included: true },
      { label: "Cambios detectados", included: true },
      { label: "Histórico", included: true },
      { label: "Radar Regulatorio", included: true },
      { label: "Portfolio y competidores", included: true },
      { label: "Alertas por email instantáneas", included: true },
      { label: "Resumen semanal por email", included: true },
      { label: "Predicciones de precio con IA", included: true },
      { label: "Comparador de precios vs. competencia", included: true },
      { label: "Ranking de oportunidad", included: true },
      { label: "20 usuarios", included: true },
    ],
  },
];

const FEATURE_BANNERS: Record<string, string> = {
  alerts: "Las alertas por email están disponibles en el plan PRO",
  trends: "La tendencia de precios está disponible en el plan ULTRA",
  prices: "El comparador de precios frente a la competencia está disponible en el plan ULTRA",
  reports: "Los informes personalizados en PDF están disponibles en el plan PRO",
};

const Check = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

export default function UpgradePage() {
  const [searchParams] = useSearchParams();
  const feature = searchParams.get("feature") ?? "";
  const banner = FEATURE_BANNERS[feature];

  const plan = useAuthStore(s => s.user?.plan);
  const orgName = useAuthStore(s => s.user?.organization_name) || "";
  const primaryColor = useAuthStore(s => s.user?.primary_color) || G;
  const { isMobile } = useBreakpoint();

  const currentIdx = plan ? PLAN_ORDER.indexOf(plan) : -1;
  // Plan recomendado: el siguiente al actual (si no hay plan conocido, PRO)
  const recommendedIdx = currentIdx >= 0 && currentIdx < PLAN_ORDER.length - 1
    ? currentIdx + 1
    : (currentIdx === -1 ? 1 : -1);

  const mailtoFor = (planName: string) =>
    `mailto:luis@haltrixpartners.com?subject=${encodeURIComponent(`Upgrade HelixScan - ${orgName} a ${planName}`)}`;

  return (
    <div style={{ padding: isMobile ? "1.25rem 1rem" : "2rem 2.5rem", maxWidth: 1100, margin: "0 auto" }}>
      {/* Banner contextual */}
      {banner && (
        <div style={{
          marginBottom: "1.5rem",
          padding: "0.85rem 1.25rem",
          borderRadius: 18,
          background: "var(--clr-surface)",
          border: "1px solid var(--clr-border)",
          backdropFilter: "var(--clr-blur)",
          WebkitBackdropFilter: "var(--clr-blur)",
          boxShadow: "var(--clr-shadow-sm)",
          color: "var(--clr-text)",
          fontSize: "0.88rem",
          fontWeight: 500,
        }}>
          {banner}
        </div>
      )}

      {/* Cabecera */}
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h1 style={{ fontSize: isMobile ? "1.3rem" : "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", margin: 0 }}>
          Desbloquea todo el potencial de HelixScan
        </h1>
        <p style={{ fontSize: "0.82rem", color: "var(--clr-text3)", marginTop: 8 }}>
          Elige el plan que mejor se adapte a tu organización.
        </p>
      </div>

      {/* Cards de planes */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
        gap: "1.25rem",
        alignItems: "stretch",
      }}>
        {PLANS.map((p, idx) => {
          const isCurrent = idx === currentIdx;
          const isRecommended = idx === recommendedIdx;
          const isUpgrade = currentIdx >= 0 ? idx > currentIdx : idx > 0;

          return (
            <div
              key={p.id}
              style={{
                background: "var(--clr-surface)",
                border: "1px solid var(--clr-border)",
                borderRadius: 18,
                backdropFilter: "var(--clr-blur)",
                WebkitBackdropFilter: "var(--clr-blur)",
                boxShadow: "var(--clr-shadow-sm)",
                padding: "1.5rem 1.4rem",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              {/* Badge recomendado */}
              {isRecommended && (
                <div style={{
                  position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)",
                  background: primaryColor, color: "#fff",
                  fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.8px", textTransform: "uppercase",
                  padding: "3px 12px", borderRadius: 999, whiteSpace: "nowrap",
                }}>
                  Recomendado
                </div>
              )}

              {/* Nombre del plan */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: "1.1rem", fontWeight: 700, letterSpacing: "1px", color: "var(--clr-text)" }}>
                  {p.name}
                </span>
                {isCurrent && (
                  <span style={{
                    fontSize: "0.62rem", fontWeight: 500, letterSpacing: "0.5px", textTransform: "uppercase",
                    color: "var(--clr-text3)", border: "1px solid var(--clr-border)",
                    borderRadius: 999, padding: "2px 8px",
                  }}>
                    Tu plan actual
                  </span>
                )}
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--clr-text3)", marginBottom: "1.1rem" }}>
                {p.tagline}
              </div>

              {/* Lista de features */}
              <ul style={{ listStyle: "none", margin: 0, padding: 0, flex: 1 }}>
                {p.features.map(f => (
                  <li
                    key={f.label}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "0.32rem 0",
                      fontSize: "0.82rem",
                      color: f.included ? "var(--clr-text2)" : "var(--clr-text4)",
                    }}
                  >
                    {f.included ? (
                      <span style={{ color: "var(--clr-text2)", display: "inline-flex" }}>{Check}</span>
                    ) : (
                      <span style={{ width: 14, textAlign: "center", flexShrink: 0, opacity: 0.6 }}>—</span>
                    )}
                    {f.label}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {isUpgrade && (
                <a
                  href={mailtoFor(p.name)}
                  style={{
                    marginTop: "1.25rem",
                    display: "block",
                    textAlign: "center",
                    textDecoration: "none",
                    background: isRecommended ? primaryColor : "var(--clr-surface)",
                    color: isRecommended ? "#fff" : "var(--clr-text2)",
                    border: isRecommended ? "1px solid transparent" : "1px solid var(--clr-border)",
                    borderRadius: 999,
                    padding: "0.6rem 1rem",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = "0.85")}
                  onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
                >
                  Solicitar upgrade
                </a>
              )}
            </div>
          );
        })}
      </div>

      {/* Pie */}
      <p style={{ textAlign: "center", fontSize: "0.8rem", color: "var(--clr-text4)", marginTop: "1.75rem" }}>
        Sin permanencia. El cambio de plan se activa en menos de 24h.
      </p>
    </div>
  );
}
